import React, { useEffect, useState, useMemo } from 'react';
import { Avatar, Tooltip } from 'antd';
import AppSpinner from './shared/AppSpinner';
import {
  CheckCircleOutlined,
  MessageOutlined,
  UserAddOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  DownOutlined,
  UpOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { debounce } from 'lodash';
import { activityIcon } from '../configs/designTokens';
import '../styles/index.scss';

// Activity type configurations with icons
const ACTIVITY_CONFIG = {
  CREATED: { label: 'created', icon: PlusCircleOutlined, color: activityIcon.success },
  COMPLETED: { label: 'completed', icon: CheckCircleOutlined, color: activityIcon.success },
  COMMENT: { label: 'commented on', icon: MessageOutlined, color: activityIcon.info },
  ASSIGNED: { label: 'assigned', icon: UserAddOutlined, color: activityIcon.purple },
  STATUS: { label: 'updated status of', icon: EditOutlined, color: activityIcon.orange },
  URGENCY: { label: 'changed priority of', icon: ThunderboltOutlined, color: activityIcon.red },
  DETAILS: { label: 'updated details of', icon: EditOutlined, color: activityIcon.orange },
  DEADLINE: { label: 'updated deadline for', icon: ClockCircleOutlined, color: activityIcon.magenta },
  UPDATED: { label: 'updated', icon: EditOutlined, color: activityIcon.gray },
};

// Filter options
const FILTER_OPTIONS = [
  { key: 'all', label: 'All Activity' },
  { key: 'comments', label: 'Comments' },
  { key: 'status', label: 'Status Changes' },
  { key: 'assignments', label: 'Assignments' },
];

export const TaskRecentActivity = () => {
  const {
    fetchHistories,
    histories = [],
    activity,
    currentUser,
    fetchTaskByIds,
    taskByIdsLoading,
    showMyTasks,
    setActivity,
  } = useTaskContext();

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [activityHistories, setActivityHistories] = useState([]);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useEffect(() => {
    if (!currentUser?.role?.org?.id) return;

    const orgId = currentUser.role.org.id;
    const orgLevel = currentUser.role.org.level;

    const orgFilter = orgLevel === 'PLATFORM'
      ? {
          author: {
            roles_some: {
              org: {
                OR: [
                  { id: orgId },
                  { parentOrg_some: { id: orgId } },
                ],
              },
            },
          },
        }
      : {
          author: {
            roles_some: {
              org: { id: orgId },
            },
          },
        };

    setInitialFetchDone(false);
    fetchHistories({
      variables: {
        where: {
          AND: [
            { type: 'TASK' },
            orgFilter,
          ],
        },
        orderBy: 'createdAt_DESC',
        first: 100,
      },
    });
  }, [fetchHistories, currentUser]);

  // Snapshot histories from the all-tasks fetch into local state
  useEffect(() => {
    if (!initialFetchDone && histories.length > 0) {
      setActivityHistories(histories);
      setInitialFetchDone(true);
    }
  }, [histories, initialFetchDone]);

  const getActivityType = (history) => {
    if (history.action === 'CREATED') return 'CREATED';
    if (history.action === 'ADDED') return 'COMMENT';
    if (history.action === 'UPDATED') {
      const fieldName = history.data?.fieldName?.split('.')[0];
      switch (fieldName) {
        case 'assigneeUsers': return 'ASSIGNED';
        case 'status':
          return history.data.newValue === 'COMPLETED' ? 'COMPLETED' : 'STATUS';
        case 'urgency': return 'URGENCY';
        case 'taskDetails': return 'DETAILS';
        case 'dueDate': return 'DEADLINE';
        default: return 'UPDATED';
      }
    }
    return 'UPDATED';
  };

  const allowedTaskIds = activity.map((task) => task.id);
  const filteredHistories = activityHistories.filter((history) =>
    allowedTaskIds.includes(history.referenceId)
  );

  // Process and group activities
  const processedActivities = useMemo(() => {
    const getTaskName = (referenceId) => {
      const foundTask = activity.find((t) => t.id === referenceId);
      return foundTask ? foundTask.name : 'Unknown Task';
    };

    const activities = filteredHistories
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map((history) => {
        const type = getActivityType(history);
        const config = ACTIVITY_CONFIG[type];
        return {
          id: history.id,
          type,
          config,
          taskId: history.referenceId,
          taskName: getTaskName(history.referenceId),
          user: history.author?.name || 'Unknown',
          time: moment(history.createdAt),
          timeAgo: moment(history.createdAt).fromNow(),
        };
      });

    // Apply filter
    let filtered = activities;
    if (activeFilter === 'comments') {
      filtered = activities.filter((a) => a.type === 'COMMENT');
    } else if (activeFilter === 'status') {
      filtered = activities.filter((a) => ['STATUS', 'COMPLETED'].includes(a.type));
    } else if (activeFilter === 'assignments') {
      filtered = activities.filter((a) => a.type === 'ASSIGNED');
    }

    return filtered;
  }, [filteredHistories, activeFilter, activity]);

  // Group by date
  const groupedActivities = useMemo(() => {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const thisWeek = moment().subtract(7, 'days').startOf('day');

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    processedActivities.forEach((activity) => {
      if (activity.time.isSameOrAfter(today)) {
        groups.today.push(activity);
      } else if (activity.time.isSameOrAfter(yesterday)) {
        groups.yesterday.push(activity);
      } else if (activity.time.isSameOrAfter(thisWeek)) {
        groups.thisWeek.push(activity);
      } else {
        groups.older.push(activity);
      }
    });

    return groups;
  }, [processedActivities]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const todayCount = groupedActivities.today.length;
    const completed = processedActivities.filter((a) => a.type === 'COMPLETED').length;
    const comments = processedActivities.filter((a) => a.type === 'COMMENT').length;
    const updates = processedActivities.filter((a) =>
      !['COMPLETED', 'COMMENT'].includes(a.type)
    ).length;

    return { todayCount, completed, comments, updates, total: processedActivities.length };
  }, [processedActivities, groupedActivities]);

  useEffect(() => {
    const referenceIds = activityHistories.map((h) => h.referenceId);
    const idArray = referenceIds.length > 0 ? referenceIds : [];

    if (idArray.length > 0 && !(showMyTasks && currentUser.isPreviewOrg)) {
      const handleFetchTasksPaginated = debounce(() => {
        fetchTaskByIds({
          variables: {
            where: {
              AND: [
                { id_in: idArray },
                {
                  OR: [
                    {
                      AND: [
                        ...(showMyTasks && !currentUser.isPreviewOrg
                          ? [{ assigneeUsers_some: { id: currentUser.id } }]
                          : []),
                        {
                          createdBy: {
                            roles_some: {
                              org: {
                                OR: [
                                  { parentOrg_some: { id: currentUser.role.org.id } },
                                  { id: currentUser.role.org.id },
                                ],
                              },
                            },
                          },
                        },
                      ],
                    },
                    ...(showMyTasks
                      ? [
                          { createdBy: { id: currentUser.id } },
                          { followers_some: { id: currentUser.id } },
                          { collaborators_some: { id: currentUser.id } },
                        ]
                      : []),
                  ],
                },
              ],
            },
            orderBy: 'updatedAt_DESC',
            first: 100,
            skip: 0,
          },
        });
      }, 500);

      handleFetchTasksPaginated();
    } else if (showMyTasks && currentUser.isPreviewOrg) {
      setActivity([]);
    }
  }, [
    activityHistories,
    fetchTaskByIds,
    currentUser.role.org.id,
    currentUser.id,
    showMyTasks,
    currentUser.isPreviewOrg,
    setActivity,
  ]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  const renderActivityItem = (activity) => {
    const IconComponent = activity.config.icon;

    return (
      <div key={activity.id} className="task-activity-item">
        <div
          className="task-activity-item__icon"
          style={{ color: activity.config.color }}
        >
          <IconComponent />
        </div>
        <div className="task-activity-item__content">
          <span className="task-activity-item__user">{activity.user}</span>
          <span className="task-activity-item__action"> {activity.config.label} </span>
          <Link
            to={`/tasks/details/${activity.taskId}`}
            className="task-activity-item__task"
          >
            {activity.taskName}
          </Link>
        </div>
        <div className="task-activity-item__time">
          {activity.timeAgo}
        </div>
      </div>
    );
  };

  const renderActivityGroup = (title, activities, startIndex = 0) => {
    if (activities.length === 0) return null;

    const visibleActivities = activities.slice(0, Math.max(0, visibleCount - startIndex));
    if (visibleActivities.length === 0) return null;

    return (
      <div className="task-activity-group">
        <div className="task-activity-group__title">{title}</div>
        <div className="task-activity-group__list">
          {visibleActivities.map(renderActivityItem)}
        </div>
      </div>
    );
  };

  // Calculate visible items per group
  const getVisibleGroups = () => {
    let remaining = visibleCount;
    const groups = [];

    if (groupedActivities.today.length > 0 && remaining > 0) {
      const items = groupedActivities.today.slice(0, remaining);
      groups.push({ title: 'Today', items });
      remaining -= items.length;
    }

    if (groupedActivities.yesterday.length > 0 && remaining > 0) {
      const items = groupedActivities.yesterday.slice(0, remaining);
      groups.push({ title: 'Yesterday', items });
      remaining -= items.length;
    }

    if (groupedActivities.thisWeek.length > 0 && remaining > 0) {
      const items = groupedActivities.thisWeek.slice(0, remaining);
      groups.push({ title: 'This Week', items });
      remaining -= items.length;
    }

    if (groupedActivities.older.length > 0 && remaining > 0) {
      const items = groupedActivities.older.slice(0, remaining);
      groups.push({ title: 'Older', items });
    }

    return groups;
  };

  const hasMore = visibleCount < processedActivities.length;

  if (taskByIdsLoading) {
    return (
      <div className="task-activity-bar task-activity-bar--loading">
        <AppSpinner size="small" />
        <span>Loading activity...</span>
      </div>
    );
  }

  return (
    <div className={`task-activity-bar ${isExpanded ? 'task-activity-bar--expanded' : ''}`}>
      {/* Summary Bar (Always Visible) */}
      <div
        className="task-activity-bar__summary"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="task-activity-bar__summary-left">
          <span className="task-activity-bar__summary-icon">
            <ClockCircleOutlined />
          </span>
          <span className="task-activity-bar__summary-text">
            {summaryStats.total === 0 ? (
              'No recent activity'
            ) : (
              <>
                <strong>{summaryStats.todayCount}</strong> activities today
                {summaryStats.completed > 0 && (
                  <span className="task-activity-bar__stat">
                    <CheckCircleOutlined /> {summaryStats.completed} completed
                  </span>
                )}
                {summaryStats.comments > 0 && (
                  <span className="task-activity-bar__stat">
                    <MessageOutlined /> {summaryStats.comments} comments
                  </span>
                )}
              </>
            )}
          </span>
        </div>
        <div className="task-activity-bar__summary-right">
          {isExpanded && (
            <div className="task-activity-bar__filter">
              <button
                className="task-activity-bar__filter-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilterMenu(!showFilterMenu);
                }}
              >
                <FilterOutlined />
                <span>{FILTER_OPTIONS.find((f) => f.key === activeFilter)?.label}</span>
                <DownOutlined className="task-activity-bar__filter-arrow" />
              </button>
              {showFilterMenu && (
                <div className="task-activity-bar__filter-menu">
                  {FILTER_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      className={`task-activity-bar__filter-option ${activeFilter === option.key ? 'task-activity-bar__filter-option--active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveFilter(option.key);
                        setShowFilterMenu(false);
                        setVisibleCount(5);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button className="task-activity-bar__toggle">
            {isExpanded ? <UpOutlined /> : <DownOutlined />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="task-activity-bar__content">
          {processedActivities.length === 0 ? (
            <div className="task-activity-bar__empty">
              No activity found
            </div>
          ) : (
            <>
              {getVisibleGroups().map((group) => (
                <div key={group.title} className="task-activity-group">
                  <div className="task-activity-group__title">{group.title}</div>
                  <div className="task-activity-group__list">
                    {group.items.map(renderActivityItem)}
                  </div>
                </div>
              ))}

              {hasMore && (
                <button
                  className="task-activity-bar__load-more"
                  onClick={handleLoadMore}
                >
                  Load more activity
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskRecentActivity;
